import { useRef, useMemo, useCallback, memo } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useProfile } from '../hooks/useProfile';
import UserAvatar from '../components/ui/UserAvatar';
import type {
  ProfileStats,
  ProfileHistoryEntry,
  BestWpm,
  SpeedRating,
  AccuracyRating,
} from '@codedrill/shared';

// ── Pure helpers (called once, never on re-render of children) ──────────────

function formatMs(ms: number): string {
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return '<1m';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: '2-digit',
  });
}

function fmtLang(lang: string): string {
  if (lang === 'cpp') return 'C++';
  return lang.toUpperCase();
}

const SPEED_LABEL: Record<SpeedRating, string> = {
  beginner:     'Beginner',
  intermediate: 'Intermediate',
  advanced:     'Advanced',
  expert:       'Expert',
  master:       'Master',
};

const ACCURACY_LABEL: Record<AccuracyRating, string> = {
  low:      'Low',
  moderate: 'Moderate',
  good:     'Good',
  great:    'Great',
  perfect:  'Perfect',
};

const SPEED_COLOR: Record<SpeedRating, string> = {
  beginner:     'var(--text-dim)',
  intermediate: 'var(--correct)',
  advanced:     'var(--accent)',
  expert:       '#f0a500',
  master:       '#e040fb',
};

// ── Sub-components — all memo-wrapped ───────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}

const StatCard = memo(function StatCard({ label, value, sub, accent }: StatCardProps) {
  return (
    <div className="profile-stat-card">
      <span className="profile-stat-label">{label}</span>
      <span className="profile-stat-value" style={accent ? { color: accent } : undefined}>
        {value}
      </span>
      {sub && <span className="profile-stat-sub">{sub}</span>}
    </div>
  );
});

interface XPBarProps {
  xp: number;
  xpToNext: number;
  level: number;
}

const XPBar = memo(function XPBar({ xp, xpToNext, level }: XPBarProps) {
  const pct = Math.min(100, Math.round((xp / xpToNext) * 100));
  return (
    <div className="xp-bar-wrap">
      <div className="xp-bar-header">
        <span className="xp-bar-level">Lv {level}</span>
        <span className="xp-bar-progress">{xp} / {xpToNext} XP</span>
      </div>
      <div className="xp-bar-track">
        <div className="xp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
});

interface LangRowProps {
  entry: BestWpm;
}

const LangRow = memo(function LangRow({ entry }: LangRowProps) {
  return (
    <div className="lang-stat-row">
      <span className="lang-stat-name">{fmtLang(entry.language)}</span>
      <div className="lang-stat-metrics">
        <span className="lang-stat-pill lang-stat-pill--wpm">{entry.bestWpm} best</span>
        <span className="lang-stat-pill">{entry.averageWpm.toFixed(0)} avg</span>
        <span className="lang-stat-pill">{entry.averageAccuracy.toFixed(1)}% acc</span>
        <span className="lang-stat-pill lang-stat-pill--dim">{entry.sessionCount} runs</span>
      </div>
    </div>
  );
});

interface HistoryRowProps {
  entry: ProfileHistoryEntry;
}

const HistoryRow = memo(function HistoryRow({ entry }: HistoryRowProps) {
  const mins = (entry.durationMs / 60_000).toFixed(1);
  return (
    <tr className="history-row">
      <td className="history-snippet" title={entry.snippetTitle}>
        {entry.snippetTitle.length > 28
          ? entry.snippetTitle.slice(0, 26) + '…'
          : entry.snippetTitle}
      </td>
      <td className="history-lang">{fmtLang(entry.language)}</td>
      <td className="stat-wpm">{entry.wpm}</td>
      <td>{entry.accuracy.toFixed(1)}%</td>
      <td className="history-duration">{mins}m</td>
      <td className="date-col">{formatDateShort(entry.completedAt)}</td>
    </tr>
  );
});

interface OverviewSectionProps {
  stats: ProfileStats;
}

const OverviewSection = memo(function OverviewSection({ stats }: OverviewSectionProps) {
  return (
    <section>
      <h2 className="profile-section-title">overview</h2>
      <div className="profile-stats-grid">
        <StatCard label="avg wpm"       value={stats.averageWpm.toFixed(0)}      accent="var(--accent)" />
        <StatCard label="avg accuracy"  value={`${stats.averageAccuracy.toFixed(1)}%`} />
        <StatCard label="best wpm"      value={String(stats.bestWpm)}             accent="var(--correct)" />
        <StatCard label="total sessions" value={String(stats.totalSessions)} />
        <StatCard label="time typed"    value={formatMs(stats.totalTimeTypedMs)} />
        {stats.globalRank !== null && (
          <StatCard
            label="global rank"
            value={`#${stats.globalRank}`}
            sub={stats.percentileRank !== null ? `top ${100 - stats.percentileRank}%` : undefined}
            accent="var(--accent)"
          />
        )}
      </div>
    </section>
  );
});

interface RatingBadgesProps {
  speed: SpeedRating;
  accuracy: AccuracyRating;
}

const RatingBadges = memo(function RatingBadges({ speed, accuracy }: RatingBadgesProps) {
  return (
    <div className="rating-badges">
      <div className="rating-badge" style={{ borderColor: SPEED_COLOR[speed], color: SPEED_COLOR[speed] }}>
        <span className="rating-badge-label">speed</span>
        <span className="rating-badge-value">{SPEED_LABEL[speed]}</span>
      </div>
      <div className="rating-badge">
        <span className="rating-badge-label">accuracy</span>
        <span className="rating-badge-value">{ACCURACY_LABEL[accuracy]}</span>
      </div>
    </div>
  );
});

interface ProgressionSectionProps {
  stats: ProfileStats;
}

const ProgressionSection = memo(function ProgressionSection({ stats }: ProgressionSectionProps) {
  return (
    <section>
      <h2 className="profile-section-title">progression</h2>
      <div className="progression-wrap">
        <XPBar xp={stats.xp} xpToNext={stats.xpToNextLevel} level={stats.level} />
        <RatingBadges speed={stats.speedRating} accuracy={stats.accuracyRating} />
      </div>
    </section>
  );
});

interface LanguageSectionProps {
  entries: BestWpm[];
}

const LanguageSection = memo(function LanguageSection({ entries }: LanguageSectionProps) {
  if (entries.length === 0) return null;
  return (
    <section>
      <h2 className="profile-section-title">by language</h2>
      <div className="lang-stats-list">
        {entries.map((e) => <LangRow key={e.language} entry={e} />)}
      </div>
    </section>
  );
});

interface HistorySectionProps {
  history: ProfileHistoryEntry[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPage: (p: number) => void;
}

const HistorySection = memo(function HistorySection({
  history, total, page, pageSize, loading, onPage,
}: HistorySectionProps) {
  const totalPages = Math.ceil(total / pageSize);

  const handlePrev = useCallback(() => onPage(page - 1), [onPage, page]);
  const handleNext = useCallback(() => onPage(page + 1), [onPage, page]);

  return (
    <section>
      <h2 className="profile-section-title">history</h2>

      <div className="history-table-wrap" aria-busy={loading}>
        <table className="leaderboard-table history-table">
          <thead>
            <tr>
              <th>snippet</th>
              <th>lang</th>
              <th>wpm</th>
              <th>acc</th>
              <th>time</th>
              <th>date</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="leaderboard-empty">no sessions yet</td>
              </tr>
            )}
            {history.map((entry) => (
              <HistoryRow key={entry.sessionId} entry={entry} />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="profile-pagination">
          <button className="btn btn--ghost" onClick={handlePrev} disabled={page === 1 || loading}>
            prev
          </button>
          <span>{page} / {totalPages}</span>
          <button className="btn btn--ghost" onClick={handleNext} disabled={page >= totalPages || loading}>
            next
          </button>
        </div>
      )}
    </section>
  );
});

// ── Profile identity header ──────────────────────────────────────────────────

interface ProfileIdentityProps {
  username: string;
  email: string;
  avatarUrl: string | null;
  joinDate: string;
  countryCode: string | null;
  uploading: boolean;
  onAvatarClick: () => void;
}

const ProfileIdentity = memo(function ProfileIdentity({
  username, email, avatarUrl, joinDate, countryCode, uploading, onAvatarClick,
}: ProfileIdentityProps) {
  return (
    <div className="profile-identity">
      <div className="profile-avatar-wrap">
        <UserAvatar avatarUrl={avatarUrl} username={username} size="lg" />
        <button
          className="avatar-upload-btn"
          onClick={onAvatarClick}
          disabled={uploading}
          aria-label="Upload profile picture"
          title="Change avatar"
        >
          {uploading ? '…' : '+'}
        </button>
      </div>

      <div className="profile-identity-info">
        <h1 className="profile-username">
          {countryCode && (
            <span className="profile-flag" title={countryCode}>
              {countryCodeToFlag(countryCode)}
            </span>
          )}
          {username}
        </h1>
        <p className="profile-email">{email}</p>
        <p className="profile-join-date">joined {formatDate(joinDate)}</p>
      </div>
    </div>
  );
});

/** Converts ISO 3166-1 alpha-2 to a flag emoji. Pure function — no computation cost. */
function countryCodeToFlag(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

// ── Page root ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const { stats, history, total, page, loading, error, setPage, updateAvatar, uploading } =
    useProfile();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void updateAvatar(file);
      e.target.value = '';
    },
    [updateAvatar],
  );

  // Stable join date — only recompute when stats changes (which is rare)
  const joinDate = useMemo(
    () => stats?.joinDate ?? user?.createdAt ?? new Date().toISOString(),
    [stats?.joinDate, user?.createdAt],
  );

  if (loading && !stats) {
    return (
      <div className="snippet-loader">
        <span className="loader-dot" />
        <span className="loader-dot" />
        <span className="loader-dot" />
      </div>
    );
  }

  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="profile-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="avatar-file-input"
        onChange={handleFileChange}
      />

      <ProfileIdentity
        username={user?.username ?? 'profile'}
        email={user?.email ?? ''}
        avatarUrl={user?.avatarUrl ?? null}
        joinDate={joinDate}
        countryCode={stats?.countryCode ?? null}
        uploading={uploading}
        onAvatarClick={handleAvatarClick}
      />

      {stats && (
        <>
          <OverviewSection stats={stats} />
          <ProgressionSection stats={stats} />
          <LanguageSection entries={stats.bestWpmPerLanguage} />
          <HistorySection
            history={history}
            total={total}
            page={page}
            pageSize={PAGE_SIZE}
            loading={loading}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
